/**
 * Los tipos de lo que devuelve la API, escritos contra las respuestas REALES.
 *
 * No están generados de un esquema: el backend es PHP y publicar un OpenAPI
 * sería una tercera cosa que se desincroniza. Lo que sostiene que estos tipos
 * sean ciertos son las pruebas de extremo a extremo, que hablan HTTP contra la
 * pila real, y el haber capturado las respuestas de verdad antes de escribir
 * esto en lugar de deducirlas del código.
 *
 * Ojo con dos formas que sorprenden si se leen solo el handoff:
 *
 *   · Los nombres y guiones de estancia llegan **en los dos idiomas a la vez**
 *     (`{es, en}`), no traducidos por el servidor. Es lo que permite cambiar
 *     de idioma sin red y sin volver a pedir nada (decisión D6).
 *   · `/uploads/init`, `PUT .../chunk/{n}` y `GET /uploads/{id}` devuelven
 *     todos el MISMO objeto: el estado de la sesión. Así el cliente tiene una
 *     sola forma de leer "por dónde iba".
 */

import type { Idioma } from '../i18n/index.js';

export type ModoCaptura = 'video' | 'fotos' | 'libre';
export type ModoEfectivo = 'video' | 'fotos';
export type EstadoLimpieza = 'en_curso' | 'subiendo' | 'completa' | 'verificada' | 'rechazada';
export type EstadoAsignacion = 'pendiente' | 'en_curso' | 'subiendo' | 'completada';
export type EstadoEstancia = 'pendiente' | 'hecha' | 'no_accesible';
export type TipoAsset = 'video' | 'foto' | 'incidencia_foto';
export type Gravedad = 'baja' | 'media' | 'alta';
export type Plataforma = 'ios' | 'android' | 'otro';
export type Calidad = '480p' | '720p' | '1080p';

/**
 * Un texto del catálogo en todos los idiomas, como lo manda el servidor.
 *
 * Se llamaba `Bilingue`, y ese nombre dejó de ser verdad en cuanto entró el
 * alemán. `PorIdioma` no vuelve a caducar.
 *
 * ### Por qué `es` es el único obligatorio
 *
 * `nombre_es` y `nombre_en` son NOT NULL en la base desde 0003; `nombre_de`
 * llegó en 0022 y es NULL-able **a propósito**. Una instalación que ya tenía
 * pisos no puede inventarse sus nombres en alemán, y rellenarlos con el
 * español haría que la base AFIRMARA una traducción que no existe —el mismo
 * error que el distintivo de HTTPS—. Con NULL se puede preguntar cuántas
 * faltan, que es lo que enseña el editor del recorrido; con un relleno
 * automático no hay forma de distinguir «traducido» de «copiado».
 *
 * El `Partial` acepta además `undefined`, y eso hace falta de verdad: en el
 * móvil estos objetos salen de IndexedDB, y un día cacheado ANTES del alemán
 * no trae el campo. Una empleada puede llevar el día de ayer guardado y estar
 * en un piso sin cobertura. `undefined ?? es` hace lo mismo que `null ?? es`.
 */
// El `| undefined` del valor es obligatorio y no es ruido: el proyecto tiene
// `exactOptionalPropertyTypes`, asi que un campo opcional admite FALTAR pero
// no admite valer `undefined` escrito a mano. Y escrito a mano es justo como
// llega desde IndexedDB (`e.nombre_de`), que es el caso para el que existe.
export type PorIdioma = { readonly es: string } & Readonly<
    Partial<Record<Idioma, string | null | undefined>>
>;
export type PorIdiomaOpcional = Readonly<Partial<Record<Idioma, string | null | undefined>>>;

/**
 * El texto en el idioma que toca, con el español de respaldo.
 *
 * **Esta función es la única forma de leer un dato traducido.** Existía desde
 * el principio y no la llamaba NADIE: por el código había repartidos ternarios
 * de la forma `idioma === 'en' ? x.nombre_en : x.nombre_es`, uno por pantalla.
 * Un ternario binario sobre el idioma no falla cuando llega un tercero —elige
 * el español y se queda tan ancho—, así que el panel salía en alemán con los
 * nombres de las estancias en español, sin error de compilación y sin que
 * ninguna prueba lo viera. Al añadir `de` a `Idioma`, el ÚNICO sitio donde
 * saltó el compilador fue aquí dentro, precisamente porque aquí se indexa.
 */
export function enIdioma(t: PorIdioma, idioma: Idioma): string {
    return t[idioma] ?? t.es;
}

/** Igual, para un campo que puede faltar en todos los idiomas (la guía). */
export function enIdiomaOpcional(t: PorIdiomaOpcional, idioma: Idioma): string | null {
    return t[idioma] ?? t.es ?? null;
}

export interface Empleada {
    id: string;
    nombre: string;
    idioma: Idioma;
}

/**
 * Respuesta de `GET /auth/invite/{token}`: qué pide esta invitación.
 *
 * `abierta` es la diferencia que decide la pantalla 00: si es true, la persona
 * escribe su nombre y sus datos; si es false, oficina ya puso el nombre y solo
 * falta el PIN.
 */
export interface Invitacion {
    valida: boolean;
    abierta: boolean;
    nombre: string | null;
    expira_at: string | null;
    /** Por qué no vale, para elegir el texto: caducada, ya usada, inválida. */
    motivo: string | null;
    /**
     * Quién responde de los datos (0024).
     *
     * Viaja DENTRO de esta respuesta porque la pantalla de alta es la ÚNICA de
     * la app que no puede funcionar sin red: cada petición que se le añada es
     * otra cosa que puede fallar justo ahí. Y llega también cuando la
     * invitación NO vale, para que el aviso no dependa de que el enlace esté
     * bien.
     *
     * `null` es «no ha llegado», y NO es lo mismo que «no hay nada
     * configurado»: pasa en el camino de respaldo, cuando la petición falla
     * con un error que no se sabe leer y se deja seguir igual. Ahí la pantalla
     * no pinta el aviso, porque un aviso que no se ha podido leer no es un
     * aviso. Es el mismo tercer estado que el del vigía.
     */
    legal: DatosLegales | null;
}

/**
 * El aviso de protección de datos, tal y como lo compone el servidor.
 *
 * Los cinco campos de identidad son `null` hasta que alguien de oficina los
 * rellena, y `null` NO es la cadena vacía: la pantalla lo DICE en vez de dejar
 * un hueco donde tendría que ir el nombre de quien responde.
 */
export interface DatosLegales {
    titular: string | null;
    nif: string | null;
    direccion: string | null;
    contacto: string | null;
    politica_url: string | null;
    /** Los días que se guarda una prueba, VIVOS: salen de `retencion_dias`. */
    retencion_dias: number;
    /**
     * Qué pasa al vencer el plazo: `vaciar`, `anonimizar` o `borrar`.
     *
     * Al huésped no le dice nada; a la empleada sí, porque `anonimizar` es
     * literalmente «también quién la hizo». Existía desde 0007 y no se lo
     * enseñaba nadie a la persona a la que le afecta.
     */
    purga_modo: string;
}

/** Respuesta de `POST /auth/invite/redeem`. El `device_token` solo llega aquí. */
export interface Alta {
    jwt: string;
    device_token: string;
    device_id: string;
    employee: Empleada;
    expira_en: number;
}

/** Respuesta de `POST /auth/login`. */
export interface Acceso {
    jwt: string;
    employee: Empleada;
    expira_en: number;
}

export interface PuntoFoto {
    id: string;
    slug: string;
    etiqueta: PorIdioma;
    orden: number;
    obligatorio: boolean;
}

export interface Estancia {
    id: string;
    orden: number;
    nombre: PorIdioma;
    guia: PorIdiomaOpcional;
    min_fotos: number;
    min_video_s: number;
    max_video_s: number;
    puntos: PuntoFoto[];
}

export interface Apartamento {
    id: string;
    nombre: string;
    direccion: string | null;
    dormitorios: number | null;
    hora_salida: string | null;
    modo_captura: ModoCaptura;
    /** Si admite un unico video de todo el recorrido en vez de un clip por estancia. */
    video_completo: boolean;
    max_video_completo_s: number;
    notas_acceso: string | null;
    qr_token: string;
    estancias: Estancia[];
}

/** La limpieza tal como aparece dentro del día. Resumida. */
export interface LimpiezaEnDia {
    id: string;
    status: EstadoLimpieza;
    started_at: string;
    ended_at: string | null;
    estancias_total: number;
    estancias_hechas: number;
    videos: number;
    fotos: number;
    video_ms: number;
    bytes: number;
}

export interface Asignacion {
    /** Null en los apartamentos de libre asignacion: no hay fila en `assignment`. */
    assignment_id: string | null;
    /** Sale a todas y lo coge quien pueda. El dia lo pinta en su propio apartado. */
    libre: boolean;
    estado: EstadoAsignacion;
    urgente: boolean;
    hora_limite: string | null;
    notas: string | null;
    apartamento: Apartamento;
    limpieza: LimpiezaEnDia | null;
}

export interface DiaDeTrabajo {
    fecha: string;
    zona_horaria: string;
    resumen: {
        total: number;
        completadas: number;
        pendientes: number;
        subiendo: number;
        porcentaje: number;
    };
    asignaciones: Asignacion[];
    /**
     * Lo que decide la oficina y ejecuta el móvil.
     *
     * Viaja con el día porque esta respuesta es lo único que la app pide al
     * empezar el turno y se cachea en IndexedDB: una petición aparte fallaría
     * justo el día sin cobertura.
     *
     * Opcional porque un día **cacheado por una versión anterior de la app** no
     * lo trae, y ese día se sigue usando para arrancar sin red.
     */
    ajustes?: {
        /** Política de oficina: en la app no hay ningún control para esto. */
        calidad_video: Calidad;
        /** Valor inicial del conmutador de la cola, no una política. */
        solo_wifi_por_defecto: boolean;
        /** % de la cuota del dispositivo a partir del cual se avisa (D5). */
        aviso_almacenamiento_pct: number;
        /** Y el tope absoluto en MB, por si la cuota informada es enorme. */
        aviso_almacenamiento_mb_max: number;
    };
}

export interface ResolucionQr {
    property_id: string;
    assignment_id: string;
    apartamento: string;
    modo_captura: ModoCaptura;
}

export interface EstanciaDeLimpieza {
    id: string;
    room_id: string;
    nombre: string;
    orden: number;
    estado: EstadoEstancia;
    motivo_no_accesible: string | null;
    video_ms: number | null;
    fotos: number;
    cerrada_at: string | null;
}

export interface AssetDeLimpieza {
    id: string;
    cleaning_room_id: string | null;
    tipo: TipoAsset;
    path: string;
    bytes: number;
    sha256: string;
    duracion_ms: number | null;
    capturado_at: string;
}

export interface Limpieza {
    id: string;
    assignment_id: string | null;
    apartamento: string;
    empleada: string | null;
    modo_captura: ModoEfectivo;
    status: EstadoLimpieza;
    started_at: string;
    ended_at: string | null;
    notas: string | null;
    carpeta_path: string | null;
    recuento: {
        estancias: number;
        estancias_hechas: number;
        videos: number;
        fotos: number;
        video_ms: number;
        bytes: number;
        incidencias: number;
    };
    estancias: EstanciaDeLimpieza[];
    assets: AssetDeLimpieza[];
}

/** Estado de una subida troceada. Lo devuelven init, cada trozo y el GET. */
export interface EstadoSubida {
    estado: 'abierta';
    upload_id: string;
    asset_id: string;
    chunk_bytes: number;
    chunks_total: number;
    chunks_recibidos: number;
    bytes_recibidos: number;
    bytes_total: number;
    /** Los trozos que faltan. Es LO que permite reanudar sin reenviar todo. */
    pendientes: number[];
    expira_at: string;
}

export interface SubidaYaConfirmada {
    estado: 'ya_confirmado';
    asset: { id: string; path: string; sha256: string; bytes: number };
}

export interface SubidaConfirmada {
    estado: 'confirmado';
    asset: { id: string; path: string; bytes: number; sha256: string; tipo: TipoAsset };
}

export type RespuestaInicioSubida = EstadoSubida | SubidaYaConfirmada;

export interface Posicion {
    lat: number;
    lng: number;
    acc: number;
    at: string;
}
